<script setup>
import { computed } from 'vue';
import { MESSAGE_STATUS, MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';

const { contentAttributes, status, sender, messageType, createdAt } =
  useMessageContext();

const hasError = computed(() => {
  return status.value === MESSAGE_STATUS.FAILED;
});

const fromEmail = computed(() => {
  return contentAttributes.value?.email?.from ?? [];
});

const toEmail = computed(() => {
  const { toEmails, email } = contentAttributes.value;
  return email?.to ?? toEmails ?? [];
});

const ccEmail = computed(() => {
  return (
    contentAttributes.value?.ccEmails ??
    contentAttributes.value?.email?.cc ??
    []
  );
});

const senderName = computed(() => {
  const fromEmailAddress = fromEmail.value[0] ?? '';
  const senderEmail = sender.value.email ?? '';

  if (!fromEmailAddress && !senderEmail) return null;

  // if the sender of the conversation and the sender of this particular
  // email are the same, only then we return the sender name
  if (fromEmailAddress === senderEmail) {
    return sender.value.name;
  }

  return null;
});

const bccEmail = computed(() => {
  return (
    contentAttributes.value?.bccEmails ??
    contentAttributes.value?.email?.bcc ??
    []
  );
});

const subject = computed(() => {
  return contentAttributes.value?.email?.subject ?? '';
});

const showMeta = computed(() => {
  return (
    fromEmail.value[0] ||
    toEmail.value.length ||
    ccEmail.value.length ||
    bccEmail.value.length ||
    subject.value
  );
});

// Direcao: outgoing/template = enviado pela equipe; incoming = recebido.
const isOutgoing = computed(
  () =>
    messageType?.value === MESSAGE_TYPES.OUTGOING ||
    messageType?.value === MESSAGE_TYPES.TEMPLATE
);

const directionLabel = computed(() =>
  isOutgoing.value ? 'Enviado' : 'Recebido'
);
const directionIcon = computed(() =>
  isOutgoing.value ? 'i-lucide-arrow-up-right' : 'i-lucide-arrow-down-left'
);

const formatTime = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>

<template>
  <section
    v-show="showMeta"
    class="space-y-1.5 rtl:pl-9 ltr:pr-9 text-xs break-words"
    :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-11'"
  >
    <template v-if="showMeta">
      <!-- Linha 1: direcao + horario + remetente, cor diferenciada -->
      <div
        class="flex items-center gap-1.5 flex-wrap font-medium"
        :class="
          hasError
            ? 'text-n-ruby-11'
            : isOutgoing
              ? 'text-n-teal-11'
              : 'text-n-blue-11'
        "
      >
        <span :class="directionIcon" class="size-3" />
        <span>{{ directionLabel }}</span>
        <span v-if="createdAt?.value" class="text-n-slate-10 font-normal">
          · {{ formatTime(createdAt.value) }}
        </span>
        <span v-if="fromEmail[0]" class="text-n-slate-10 font-normal">
          · {{ senderName ? `${senderName} <${fromEmail[0]}>` : fromEmail[0] }}
        </span>
      </div>
      <!-- Linha 2: destinatarios -->
      <div v-if="toEmail.length" class="text-n-slate-10">
        {{ $t('EMAIL_HEADER.TO') }}: {{ toEmail.join(', ') }}
      </div>
      <div v-if="ccEmail.length" class="text-n-slate-10">
        {{ $t('EMAIL_HEADER.CC') }}: {{ ccEmail.join(', ') }}
      </div>
      <div v-if="bccEmail.length" class="text-n-slate-10">
        {{ $t('EMAIL_HEADER.BCC') }}: {{ bccEmail.join(', ') }}
      </div>
      <!-- Linha 3: assunto em destaque -->
      <div v-if="subject" class="text-n-slate-12 font-semibold pt-1">
        {{ subject }}
      </div>
    </template>
  </section>
</template>
